import {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { reportPreviewService } from "@/features/esg/services";

const ReportPreviewContext = createContext(null);

const DEFAULT_FILTERS = {
  consolidation: false,
  buSide: "",
  companies: [],
  startDate: "",
  endDate: "",
  includeAllEntities: false,
  frameworks: [],
};
const DEFAULT_CREATE_MODE = "draft";

const cloneFilters = () => ({
  ...DEFAULT_FILTERS,
  companies: [],
  frameworks: [],
});
const normalizeReport = (report) => {
  if (!report || typeof report !== "object") return report;
  const filters = report.filters || {};
  const resolvedId = report.id ?? report._id;
  return {
    ...report,
    id: resolvedId ?? report.id,
    buSide: report.buSide ?? filters.buSide ?? "",
    consolidation: report.consolidation ?? filters.consolidation ?? false,
    includeAllEntities:
      report.includeAllEntities ?? filters.includeAllEntities ?? false,
    startDate: report.startDate ?? filters.startDate ?? "",
    endDate: report.endDate ?? filters.endDate ?? "",
    companies:
      report.companies ??
      filters.entityIds ??
      filters.companies ??
      report.company ??
      [],
    entityLabels: report.entityLabels ?? filters.entityLabels ?? [],
    presentedTo: report.presentedTo ?? "",
    status: report.status ?? "draft",
    completedAt: report.completedAt ?? null,
    frameworks: Array.isArray(report.frameworks)
      ? report.frameworks
      : Array.isArray(report.frameworkCodes)
        ? report.frameworkCodes
        : Array.isArray(filters.frameworkCodes)
          ? filters.frameworkCodes
          : [],
  };
};

const normalizeReportList = (data) => {
  const items = Array.isArray(data?.items)
    ? data.items
    : Array.isArray(data?.reports)
      ? data.reports
      : Array.isArray(data)
        ? data
        : [];
  return items.map((item) => normalizeReport(item));
};

const normalizePreviewResponse = (data, fallbackFilters) => {
  if (!data || typeof data !== "object") {
    return {
      filtersApplied: fallbackFilters,
      preview: null,
    };
  }

  const filtersApplied =
    data.filtersApplied || data.filters || fallbackFilters || null;

  return {
    filtersApplied,
    preview: data.preview || data,
  };
};

export function useReportPreviewContext() {
  return useContext(ReportPreviewContext);
}

export function ReportPreviewProvider({ children }) {
  const [selectedFilters, setSelectedFilters] = useState(cloneFilters);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createMode, setCreateMode] = useState(DEFAULT_CREATE_MODE);
  const [modalReport, setModalReport] = useState(null);
  const deleteRequestsRef = useRef(new Map());

  const setFilter = useCallback((key, value) => {
    setSelectedFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const applyFilters = useCallback(async () => {
    setPreviewLoading(true);
    setPreviewError("");
    try {
      const payload = {
        filters: {
          buSide: selectedFilters.buSide,
          entityIds: selectedFilters.companies,
          includeAllEntities: Boolean(selectedFilters.includeAllEntities),
          consolidation: Boolean(selectedFilters.consolidation),
          startDate: selectedFilters.startDate,
          endDate: selectedFilters.endDate,
          frameworkCodes: Array.isArray(selectedFilters.frameworks)
            ? selectedFilters.frameworks
            : [],
        },
      };

      const data = await reportPreviewService.fetchPreview(payload, {
        throwOnError: false,
      });

      if (!data) {
        setPreviewError("Unable to load preview data.");
        return null;
      }

      const { filtersApplied, preview } = normalizePreviewResponse(
        data,
        payload.filters,
      );
      const normalizedFilters = {
        ...filtersApplied,
        companies:
          filtersApplied?.entityIds ??
          filtersApplied?.companies ??
          payload.filters.entityIds,
        includeAllEntities:
          filtersApplied?.includeAllEntities ??
          payload.filters.includeAllEntities,
        frameworks: Array.isArray(filtersApplied?.frameworkCodes)
          ? filtersApplied.frameworkCodes
          : payload.filters.frameworkCodes,
      };

      setAppliedFilters(normalizedFilters);
      setPreviewData(preview);
      return preview;
    } catch (error) {
      setPreviewError(error?.message || "Unable to load preview data.");
      return null;
    } finally {
      setPreviewLoading(false);
    }
  }, [selectedFilters]);

  const resetFilters = useCallback(() => {
    setSelectedFilters(cloneFilters());
    setAppliedFilters(null);
    setPreviewData(null);
    setPreviewError("");
  }, []);

  const openCreateModal = useCallback(
    (mode = DEFAULT_CREATE_MODE, report = null) => {
      setCreateMode(
        mode === "completed"
          ? "completed"
          : mode === "edit-draft"
            ? "edit-draft"
            : DEFAULT_CREATE_MODE,
      );
      setModalReport(report || null);
      setIsCreateModalOpen(true);
    },
    [],
  );

  const closeCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
    setCreateMode(DEFAULT_CREATE_MODE);
    setModalReport(null);
  }, []);

  const createReport = useCallback(
    async ({
      name,
      description,
      presentedTo,
      includeCoverPage,
      includeContentPage,
      isShared,
    }) => {
      if (!name?.trim()) return null;

      const sourceFilters = appliedFilters || selectedFilters;
      const payload = {
        name: name.trim(),
        description: description?.trim?.() || "",
        presentedTo: presentedTo?.trim?.() || "",
        includeCoverPage: Boolean(includeCoverPage),
        includeContentPage: Boolean(includeContentPage),
        isShared: Boolean(isShared),
        filters: {
          buSide: sourceFilters?.buSide,
          entityIds: Array.isArray(sourceFilters?.companies)
            ? sourceFilters.companies
            : [],
          includeAllEntities: Boolean(sourceFilters?.includeAllEntities),
          consolidation: Boolean(sourceFilters?.consolidation),
          startDate: sourceFilters?.startDate,
          endDate: sourceFilters?.endDate,
          frameworkCodes: Array.isArray(sourceFilters?.frameworks)
            ? sourceFilters.frameworks
            : [],
        },
      };

      const data = await reportPreviewService.createReport(payload, {
        throwOnError: false,
      });
      if (!data) return null;
      const report = normalizeReport(data.report || data);
      setReports((prev) => [
        report,
        ...prev.filter((item) => item.id !== report.id),
      ]);
      return report;
    },
    [appliedFilters, selectedFilters],
  );

  const updateReport = useCallback(async (reportId, updates = {}) => {
    if (!reportId) return null;
    const data = await reportPreviewService.updateReport(reportId, updates, {
      throwOnError: false,
    });
    if (!data) return null;
    const normalized = normalizeReport(data.report || data);
    setReports((prev) =>
      prev.map((report) => (report.id === reportId ? normalized : report)),
    );
    return normalized;
  }, []);

  const completeReport = useCallback(async (reportId) => {
    if (!reportId) return null;
    const data = await reportPreviewService.completeReport(reportId, {
      throwOnError: false,
    });
    if (!data) return null;
    const normalized = normalizeReport(data.report || data);
    setReports((prev) =>
      prev.map((report) => (report.id === reportId ? normalized : report)),
    );
    return normalized;
  }, []);

  const createCompletedReport = useCallback(
    async (payload = {}) => {
      const draft = await createReport(payload);
      if (!draft?.id) return null;
      const completed = await completeReport(draft.id);
      return {
        report: completed || draft,
        completed: Boolean(completed),
      };
    },
    [completeReport, createReport],
  );

  const saveDraftChanges = useCallback(
    async ({
      reportId,
      name,
      description,
      presentedTo,
      includeCoverPage,
      includeContentPage,
      isShared,
    }) => {
      if (!reportId) return null;
      const sourceFilters = appliedFilters || selectedFilters;
      return updateReport(reportId, {
        name: name?.trim?.() || "",
        description: description?.trim?.() || "",
        presentedTo: presentedTo?.trim?.() || "",
        includeCoverPage: Boolean(includeCoverPage),
        includeContentPage: Boolean(includeContentPage),
        isShared: Boolean(isShared),
        filters: {
          buSide: sourceFilters?.buSide,
          entityIds: Array.isArray(sourceFilters?.companies)
            ? sourceFilters.companies
            : [],
          includeAllEntities: Boolean(sourceFilters?.includeAllEntities),
          consolidation: Boolean(sourceFilters?.consolidation),
          startDate: sourceFilters?.startDate,
          endDate: sourceFilters?.endDate,
          frameworkCodes: Array.isArray(sourceFilters?.frameworks)
            ? sourceFilters.frameworks
            : [],
        },
      });
    },
    [appliedFilters, selectedFilters, updateReport],
  );

  const deleteReport = useCallback(async (reportId) => {
    if (!reportId) return false;
    if (deleteRequestsRef.current.has(reportId)) {
      return deleteRequestsRef.current.get(reportId);
    }

    const request = (async () => {
      const data = await reportPreviewService.deleteReport(reportId, {
        throwOnError: false,
      });
      if (!data) return false;
      setReports((prev) => prev.filter((report) => report.id !== reportId));
      return true;
    })();

    deleteRequestsRef.current.set(reportId, request);

    try {
      return await request;
    } finally {
      deleteRequestsRef.current.delete(reportId);
    }
  }, []);

  const loadSavedReport = useCallback(async (report) => {
    const normalized = normalizeReport(report);
    if (!normalized) return null;

    const hydratedFilters = {
      consolidation: Boolean(normalized.consolidation),
      buSide: normalized.buSide || "",
      companies: Array.isArray(normalized.companies)
        ? normalized.companies
        : [],
      startDate: normalized.startDate || "",
      endDate: normalized.endDate || "",
      includeAllEntities: Boolean(normalized.includeAllEntities),
      frameworks: Array.isArray(normalized.frameworks)
        ? normalized.frameworks
        : [],
    };

    setSelectedFilters(hydratedFilters);
    setPreviewLoading(true);
    setPreviewError("");

    try {
      const data = normalized.id
        ? await reportPreviewService.fetchReport(normalized.id, {
            throwOnError: false,
          })
        : await reportPreviewService.fetchPreview(
            {
              filters: {
                buSide: hydratedFilters.buSide,
                entityIds: hydratedFilters.companies,
                includeAllEntities: hydratedFilters.includeAllEntities,
                consolidation: hydratedFilters.consolidation,
                startDate: hydratedFilters.startDate,
                endDate: hydratedFilters.endDate,
              },
            },
            {
              throwOnError: false,
            },
          );

      if (!data) {
        setAppliedFilters(hydratedFilters);
        setSelectedFilters(hydratedFilters);
        setPreviewData(null);
        setPreviewError("Unable to load preview data.");
        return null;
      }

      const { filtersApplied, preview } = normalizePreviewResponse(data, {
        buSide: hydratedFilters.buSide,
        entityIds: hydratedFilters.companies,
        includeAllEntities: hydratedFilters.includeAllEntities,
        consolidation: hydratedFilters.consolidation,
        startDate: hydratedFilters.startDate,
        endDate: hydratedFilters.endDate,
      });
      const normalizedFilters = {
        ...hydratedFilters,
        ...filtersApplied,
        companies:
          filtersApplied?.entityIds ??
          filtersApplied?.companies ??
          hydratedFilters.companies,
        frameworks: Array.isArray(filtersApplied?.frameworkCodes)
          ? filtersApplied.frameworkCodes
          : hydratedFilters.frameworks,
      };

      setAppliedFilters(normalizedFilters);
      setSelectedFilters({
        buSide: normalizedFilters.buSide || hydratedFilters.buSide || "",
        companies: Array.isArray(normalizedFilters.companies)
          ? normalizedFilters.companies
          : hydratedFilters.companies,
        startDate:
          normalizedFilters.startDate || hydratedFilters.startDate || "",
        endDate: normalizedFilters.endDate || hydratedFilters.endDate || "",
        consolidation: Boolean(
          normalizedFilters.consolidation ?? hydratedFilters.consolidation,
        ),
        includeAllEntities: Boolean(
          normalizedFilters.includeAllEntities ??
          hydratedFilters.includeAllEntities,
        ),
        frameworks: Array.isArray(normalizedFilters.frameworks)
          ? normalizedFilters.frameworks
          : Array.isArray(normalizedFilters.frameworkCodes)
            ? normalizedFilters.frameworkCodes
            : hydratedFilters.frameworks,
      });
      setPreviewData(preview);
      return preview;
    } catch (error) {
      setAppliedFilters(hydratedFilters);
      setSelectedFilters(hydratedFilters);
      setPreviewData(null);
      setPreviewError(error?.message || "Unable to load preview data.");
      return null;
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const exportReport = useCallback(
    async (reportId, format) => {
      if (!reportId || !format) return null;
      try {
        const report = reports.find((item) => item.id === reportId) || null;
        return await reportPreviewService.downloadExport(
          reportId,
          { format },
          { report },
        );
      } catch {
        return null;
      }
    },
    [reports],
  );

  const fetchReports = useCallback(async () => {
    setReportsLoading(true);
    setReportsError("");
    try {
      const data = await reportPreviewService.listReports(
        {},
        {
          throwOnError: false,
        },
      );
      if (!data) {
        setReportsError("Unable to load reports.");
        return [];
      }
      const list = normalizeReportList(data);
      setReports(list);
      return list;
    } catch (error) {
      setReportsError(error?.message || "Unable to load reports.");
      return [];
    } finally {
      setReportsLoading(false);
    }
  }, []);

  const value = useMemo(
    () => ({
      selectedFilters,
      appliedFilters,
      previewData,
      previewLoading,
      previewError,
      reports,
      reportsLoading,
      reportsError,
      isCreateModalOpen,
      createMode,
      modalReport,
      setFilter,
      applyFilters,
      resetFilters,
      openCreateModal,
      closeCreateModal,
      createReport,
      createCompletedReport,
      saveDraftChanges,
      updateReport,
      completeReport,
      deleteReport,
      loadSavedReport,
      exportReport,
      fetchReports,
    }),
    [
      selectedFilters,
      appliedFilters,
      previewData,
      previewLoading,
      previewError,
      reports,
      reportsLoading,
      reportsError,
      isCreateModalOpen,
      createMode,
      modalReport,
      setFilter,
      applyFilters,
      resetFilters,
      openCreateModal,
      closeCreateModal,
      createReport,
      createCompletedReport,
      saveDraftChanges,
      updateReport,
      completeReport,
      deleteReport,
      loadSavedReport,
      exportReport,
      fetchReports,
    ],
  );

  return (
    <ReportPreviewContext.Provider value={value}>
      {children}
    </ReportPreviewContext.Provider>
  );
}

export { ReportPreviewContext };
